import { useEffect, useRef, useState } from 'react';

import {
  Archive03Icon,
  ArrowLeft01Icon,
  ArtificialIntelligence08Icon,
  Copy01Icon,
  Delete02Icon,
  LicenseThirdPartyIcon,
  Pdf01Icon,
  PlayCircleIcon,
  RestoreBinIcon,
  SentIcon,
  Settings01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FormTypes } from 'constants/forms';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { Button, EditableTitle } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

const QuestionnaireHeader = ({
  form,
  company,
  aiGenerators,
  onAIQuestionnaireGenerator = () => {},
  onJDAIGenerator = () => {},
  onCopy = () => {},
  onArchive = () => {},
  onDelete = () => {},
  onRestore = () => {},
  onPreview = () => {},
  onPublish = () => {},
  onTitleChange = () => {},
  onOpenSettings = () => {},
}) => {
  const navigate = useNavigate();

  const [showPreview, setShowPreview] = useState(false);
  const [title, setTitle] = useState(form?.name ?? '');
  const titleInputRef = useRef(null);

  useEffect(() => {
    setTitle(form?.name ?? '');
  }, [form?.name]);

  if (!form) {
    return null;
  }

  const registerTitle = () => ({
    ref: el => {
      titleInputRef.current = el;
    },
    onChange: e => {
      const value = e.target.value;
      setTitle(value);
      onTitleChange(value);
    },
    onBlur: e => {
      const value = e.target.value;
      setTitle(value);
      onTitleChange(value);
    },
  });

  const handleArchive = () => {
    onArchive(form);
  };

  const handleDelete = () => {
    onDelete(form);
  };

  const handleRestore = () => {
    onRestore(form);
  };

  const handlePreview = () => {
    setShowPreview(true);
    onPreview();
  };

  const actions = [
    // AI PDF generator
    !form.archived &&
      company?.ff_ai_forms &&
      form?.type === FormTypes.REFEREE &&
      aiGenerators && {
        icon: Pdf01Icon,
        prefix: 'fa',
        tip: 'Generate from PDF',
        color: 'primary-4',
        onClick: onAIQuestionnaireGenerator,
      },
    // AI Job Description generator
    !form.archived &&
      company?.ff_ai_forms &&
      form?.type === FormTypes.REFEREE &&
      aiGenerators && {
        icon: ArtificialIntelligence08Icon,
        prefix: 'fa',
        tip: 'Generate from Job Description',
        color: 'primary-4',
        onClick: onJDAIGenerator,
      },
    // Form settings
    !form.archived && {
      icon: Settings01Icon,
      tip: 'Form settings',
      onClick: onOpenSettings,
    },
    // Copy Form
    !form.archived &&
      form.id && {
        icon: Copy01Icon,
        tip: 'Make a copy',
        onClick: () => onCopy(form),
      },
    // Archive Form
    !form.archived && {
      icon: Archive03Icon,
      tip: 'Archive Form',
      onClick: handleArchive,
    },
    // Delete Form
    form.id && {
      icon: Delete02Icon,
      tip: 'Delete Form',
      color: 'red-2',
      onClick: handleDelete,
    },
    // Restore Form
    form.archived && {
      icon: RestoreBinIcon,
      tip: 'Restore Form',
      color: 'orange-2',
      onClick: handleRestore,
    },
  ].filter(Boolean); // remove null/false values
  return (
    <header className="fixed top-0 left-0 right-0 h-header bg-neutral-2 border-b border-neutral-4 z-50 flex items-center justify-between px-8">
      <div className="flex items-center gap-4">
        <HugeiconsIcon
          icon={ArrowLeft01Icon}
          size="32"
          onClick={() => navigate('/questionnaires-v2')}
          className="cursor-pointer text-neutral-5"
        />
        <IconComponent icon={LicenseThirdPartyIcon} />
        <div className="flex flex-col gap-1">
          <EditableTitle
            register={registerTitle}
            name="name"
            value={title}
            disabled={form?.archived}
          />
          {form?.updated_at && (
            <span className="text-neutral-6 text-lg">
              Last updated: {moment(form.updated_at).format('DD/MM/YYYY HH:mm')}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-8">
        {actions.map((action, idx) => (
          <OverlayTrigger
            key={idx}
            trigger={['hover', 'focus']}
            placement="bottom"
            overlay={<Tooltip>{action.tip}</Tooltip>}>
            <HugeiconsIcon
              icon={action.icon}
              onClick={action.onClick}
              className={`cursor-pointer ${action.color ? `text-${action.color}` : 'text-neutral-6'}`}
              size="24"
            />
          </OverlayTrigger>
        ))}
        <div className="flex items-center gap-4">
          <Button
            type="secondary-reverse"
            className="text-neutral-6 cursor-pointer gap-2"
            onClick={handlePreview}>
            <HugeiconsIcon icon={PlayCircleIcon} size="24" /> Preview
          </Button>
          <Button className="cursor-pointer gap-2" onClick={() => onPublish(title)}>
            <HugeiconsIcon icon={SentIcon} size="24" /> Save
          </Button>
        </div>
      </div>
    </header>
  );
};

export default QuestionnaireHeader;
